import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import type { BillingCycle, Subscription, SubscriptionInput } from "../types/subscription";
import { CURRENCIES, CURRENCY_CODES } from "../lib/currencies";
import { addSavedCategory } from "../lib/categories";
import { useUserSettings } from "../lib/settings-context";
import type { UserSettingsState } from "../lib/user-settings";
import { CategoryField } from "./CategoryField";
import { cn } from "../lib/cn";

const cycles: { value: BillingCycle; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "custom_days", label: "Custom period (days)" },
];

function fromEmptyDefaults(
  s: Pick<
    UserSettingsState,
    "defaultCurrency" | "defaultBillingCycle" | "defaultCategory"
  >,
): SubscriptionInput {
  return {
    name: "",
    amount: 0,
    currency: s.defaultCurrency,
    billing_cycle: s.defaultBillingCycle,
    custom_period_days: 30,
    category: s.defaultCategory,
    next_renewal_date: new Date().toISOString().slice(0, 10),
    notes: "",
    active: true,
  };
}

function fromSubscription(s: Subscription): SubscriptionInput {
  return {
    name: s.name,
    amount: s.amount,
    currency: s.currency,
    billing_cycle: s.billing_cycle,
    custom_period_days: s.custom_period_days ?? 30,
    category: s.category,
    next_renewal_date: s.next_renewal_date,
    notes: s.notes ?? "",
    active: s.active === 1,
  };
}

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  initial?: Subscription | null;
  onSubmit: (input: SubscriptionInput) => Promise<void>;
};

export function SubscriptionDialog({
  open,
  onOpenChange,
  mode,
  initial,
  onSubmit,
}: Props) {
  const { settings, patchSettings } = useUserSettings();
  const [form, setForm] = useState<SubscriptionInput>(() =>
    fromEmptyDefaults(settings),
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm(
        initial ? fromSubscription(initial) : fromEmptyDefaults(settings),
      );
    }
  }, [
    open,
    initial,
    settings.defaultCurrency,
    settings.defaultBillingCycle,
    settings.defaultCategory,
  ]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!(form.amount > 0)) {
      setError("Amount must be greater than zero.");
      return;
    }
    if (!form.next_renewal_date) {
      setError("Next renewal date is required.");
      return;
    }
    if (form.billing_cycle === "custom_days") {
      if (!form.custom_period_days || form.custom_period_days <= 0) {
        setError("Enter a valid period in days.");
        return;
      }
    }
    setPending(true);
    try {
      const currency = form.currency.trim().toUpperCase() || "USD";
      await onSubmit({
        ...form,
        amount: form.amount,
        currency,
      });
      patchSettings({
        savedCategoryNames: addSavedCategory(
          settings.savedCategoryNames,
          form.category,
        ),
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setPending(false);
    }
  }

  const fieldClass =
    "mt-1 w-full rounded-lg border border-border bg-surface-1 px-3 py-2.5 text-base text-text placeholder:text-text-muted focus:border-accent focus:ring-1 focus:ring-accent/40 md:py-2 md:text-sm";

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            "fixed z-50 overflow-y-auto overscroll-contain border-border bg-surface-1 shadow-2xl shadow-black/40",
            "max-md:inset-0 max-md:left-0 max-md:top-0 max-md:h-[100dvh] max-md:max-h-none max-md:w-full max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-none max-md:border-0 max-md:p-4 max-md:pt-[max(1rem,env(safe-area-inset-top))] max-md:pb-[max(1rem,env(safe-area-inset-bottom))] max-md:pl-[max(1rem,env(safe-area-inset-left))] max-md:pr-[max(1rem,env(safe-area-inset-right))]",
            "md:left-1/2 md:top-1/2 md:h-auto md:w-[min(100vw-2rem,28rem)] md:max-h-[min(90dvh,640px)] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl md:border md:p-6",
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="font-display text-lg font-semibold tracking-tight text-text">
                {mode === "create" ? "Add subscription" : "Edit subscription"}
              </Dialog.Title>
            </div>
            <Dialog.Close
              className="touch-manipulation rounded-lg p-2.5 text-text-muted hover:bg-surface-2 hover:text-text md:p-1.5"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Name
              </label>
              <input
                className={fieldClass}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Adobe Creative Cloud"
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-text-muted">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={fieldClass}
                  value={form.amount || ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }))
                  }
                />
              </div>
              <div>
                <label
                  htmlFor="sub-currency"
                  className="text-xs font-medium uppercase tracking-wide text-text-muted"
                >
                  Currency
                </label>
                <select
                  id="sub-currency"
                  className={cn(fieldClass, "cursor-pointer appearance-none bg-surface-1")}
                  value={
                    form.currency.trim()
                      ? form.currency.toUpperCase()
                      : "USD"
                  }
                  onChange={(e) =>
                    setForm((f) => ({ ...f, currency: e.target.value }))
                  }
                >
                  {!CURRENCY_CODES.has(form.currency.toUpperCase()) && form.currency.trim() !== "" && (
                    <option value={form.currency.toUpperCase()}>
                      {form.currency.toUpperCase()} (saved code)
                    </option>
                  )}
                  {CURRENCIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label
                htmlFor="billing-cycle"
                className="text-xs font-medium uppercase tracking-wide text-text-muted"
              >
                Billing cycle
              </label>
              <select
                id="billing-cycle"
                className={cn(fieldClass, "cursor-pointer appearance-none bg-surface-1")}
                value={form.billing_cycle}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    billing_cycle: e.target.value as BillingCycle,
                  }))
                }
              >
                {cycles.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            {form.billing_cycle === "custom_days" && (
              <div>
                <label className="text-xs font-medium uppercase tracking-wide text-text-muted">
                  Period (days)
                </label>
                <input
                  type="number"
                  min={1}
                  className={fieldClass}
                  value={form.custom_period_days ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      custom_period_days: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                />
              </div>
            )}

            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Category
              </label>
              <CategoryField
                value={form.category}
                onChange={(v) => setForm((f) => ({ ...f, category: v }))}
              />
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Next renewal
              </label>
              <input
                type="date"
                className={fieldClass}
                value={form.next_renewal_date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, next_renewal_date: e.target.value }))
                }
              />
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-text-muted">
                Notes
              </label>
              <textarea
                className={cn(fieldClass, "min-h-[72px] resize-y")}
                value={form.notes ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Plan name, seats, trial end…"
              />
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border bg-surface-0 text-accent focus:ring-accent"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              />
              Active (included in totals)
            </label>

            {error && (
              <p className="text-sm text-danger" role="alert">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded-lg px-4 py-2 text-sm font-medium text-text-muted hover:bg-surface-2 hover:text-text"
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={pending}
                className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-surface-0 hover:brightness-110 disabled:opacity-50"
              >
                {pending ? "Saving…" : mode === "create" ? "Add" : "Save"}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
